import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { getMotionTier, shouldAnimateMotion } from '@/lib/motion-effects';
import { cn } from '@/lib/utils';

export type TabStripItem = {
  id: string;
  label: string;
  to?: string;
  end?: boolean;
};

type TabStripProps = {
  items: TabStripItem[];
  activeId: string;
  ariaLabel: string;
  className?: string;
  onSelect?: (id: string) => void;
};

/** Tabs with a smoothly sliding highlight indicator (nav + session picker). */
export function TabStrip({ items, activeId, ariaLabel, className, onSelect }: TabStripProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef(new Map<string, HTMLElement>());
  const [indicator, setIndicator] = useState({ left: 0, width: 0, height: 0, top: 0, visible: false });

  const measure = useCallback(() => {
    const el = itemRefs.current.get(activeId);
    const container = containerRef.current;
    if (!el || !container) return;
    setIndicator({
      left: el.offsetLeft,
      width: el.offsetWidth,
      height: el.offsetHeight,
      top: el.offsetTop,
      visible: true,
    });
  }, [activeId]);

  useEffect(() => {
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (containerRef.current && ro) ro.observe(containerRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, items]);

  const setRef = (id: string) => (node: HTMLElement | null) => {
    if (node) itemRefs.current.set(id, node);
    else itemRefs.current.delete(id);
  };

  const tabClass = (selected: boolean) =>
    cn(
      'oche-press oche-interactive relative z-10 shrink-0 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-medium sm:px-3 sm:py-2 sm:text-sm',
      selected ? 'text-[var(--color-chalk)]' : 'text-[var(--color-muted)] hover:text-[var(--color-chalk)]',
    );

  return (
    <div
      ref={containerRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cn('relative flex flex-nowrap items-center gap-0.5', className)}
    >
      {indicator.visible ? (
        <span
          aria-hidden
          className={cn(
            'oche-indicator pointer-events-none absolute rounded-md border border-[var(--color-oche)]/35 bg-[var(--color-surface)] shadow-[inset_0_1px_0_0_rgb(255_255_255/0.04)]',
            shouldAnimateMotion(getMotionTier()) && getMotionTier() === 'arcade' && 'oche-tab-comet',
          )}
          style={{
            left: indicator.left,
            width: indicator.width,
            height: indicator.height,
            top: indicator.top,
          }}
        />
      ) : null}

      {items.map((item) => {
        const selected = item.id === activeId;

        if (item.to) {
          return (
            <NavLink
              key={item.id}
              to={item.to}
              end={item.end}
              ref={setRef(item.id)}
              role="tab"
              aria-selected={selected}
              className={({ isActive }) => tabClass(isActive)}
            >
              {item.label}
            </NavLink>
          );
        }

        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={selected}
            ref={setRef(item.id)}
            onClick={() => onSelect?.(item.id)}
            className={tabClass(selected)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
